import type { DocumentPipelineFilter } from '@kanbu/schema/contracts';
import { WebsitePageStatus } from '@kanbu/schema/enums';
import { useLingui } from '@lingui/react/macro';
import { Badge, cn } from '@utima/ui';
import { Workflow } from 'lucide-react';
import { memo, useMemo } from 'react';

import { StatsSummaryShell } from '@/components/statsSummary/StatsSummaryShell';
import {
  StatSummaryRow,
  type RowHighlight,
} from '@/components/statsSummary/StatSummaryRow';

import {
  useKBProcessingStatDefinitions,
  type KBProcessingStats,
  type KBReadinessStatDefinition,
  type KBWebsiteStatDefinition,
} from '../hooks/useKBProcessingStatDefinitions';
import type { KBWebsitePageFilter } from '../lib/pageFilter';

export type KBStatsSummaryProps = {
  stats: KBProcessingStats;
  onStatusFilterChange?: (status: KBWebsitePageFilter | undefined) => void;
  onDocumentPipelineFilterChange?: (
    filter: DocumentPipelineFilter | undefined,
  ) => void;
  activeDocumentPipelineFilter?: DocumentPipelineFilter;
  activeStatusFilter?: KBWebsitePageFilter;
};

function isPipelineFilterActive(
  active: DocumentPipelineFilter | undefined,
  filter: DocumentPipelineFilter | undefined,
): RowHighlight {
  if (!active || !filter) {
    return 'none';
  }

  if (active === filter) {
    return 'solid';
  }

  if (
    active === 'processing' &&
    (filter === 'processing' || filter === 'parsing' || filter === 'embedding')
  ) {
    return 'soft';
  }

  if (
    active === 'errored' &&
    (filter === 'parsingError' || filter === 'embeddingError')
  ) {
    return 'soft';
  }

  if (
    filter === 'processing' &&
    (active === 'parsing' || active === 'embedding')
  ) {
    return 'soft';
  }

  return 'none';
}

function ReadinessRow({
  stat,
  stats,
  activeDocumentPipelineFilter,
  onDocumentPipelineFilterChange,
}: {
  stat: KBReadinessStatDefinition;
  stats: KBProcessingStats;
  activeDocumentPipelineFilter?: DocumentPipelineFilter;
  onDocumentPipelineFilterChange?: (
    filter: DocumentPipelineFilter | undefined,
  ) => void;
}) {
  const count = stat.getCount(stats);
  const isInactive = count === 0;

  const highlight = isInactive
    ? 'none'
    : isPipelineFilterActive(activeDocumentPipelineFilter, stat.pipelineFilter);

  return (
    <StatSummaryRow
      title={stat.title}
      description={stat.description}
      count={count}
      highlight={highlight}
      isError={stat.isError}
      isInactive={isInactive}
      onClick={
        !isInactive && stat.pipelineFilter && onDocumentPipelineFilterChange
          ? () => onDocumentPipelineFilterChange(stat.pipelineFilter)
          : undefined
      }
    />
  );
}

function WebsiteRow({
  stat,
  stats,
  activeStatusFilter,
  onStatusFilterChange,
}: {
  stat: KBWebsiteStatDefinition;
  stats: KBProcessingStats;
  activeStatusFilter?: KBWebsitePageFilter;
  onStatusFilterChange?: (status: KBWebsitePageFilter | undefined) => void;
}) {
  const count = stat.getCount(stats);
  const isInactive = count === 0;
  const isFilterable = stat.statusFilter !== undefined;

  const isActive =
    !isInactive && isFilterable && activeStatusFilter === stat.statusFilter;

  return (
    <StatSummaryRow
      title={stat.title}
      description={stat.description}
      count={count}
      highlight={isActive ? 'solid' : 'none'}
      isTotal={stat.isTotal}
      isInactive={isInactive}
      onClick={
        !isInactive && isFilterable && onStatusFilterChange
          ? () => onStatusFilterChange(stat.statusFilter)
          : undefined
      }
    />
  );
}

export const KBStatsSummary = memo(function KBStatsSummary({
  stats,
  onStatusFilterChange,
  onDocumentPipelineFilterChange,
  activeDocumentPipelineFilter,
  activeStatusFilter,
}: KBStatsSummaryProps) {
  const { t } = useLingui();
  const { readinessStats, websiteStats } = useKBProcessingStatDefinitions();

  const errored = stats.parsingError + stats.embeddingError;
  const inProgress =
    stats.uploading +
    stats.ready +
    stats.parsing +
    stats.parsingAsync +
    stats.parsed +
    stats.embedding;

  const completePercent =
    stats.total > 0 ? Math.round((stats.done / stats.total) * 100) : 0;

  const isFullyComplete =
    stats.total > 0 && stats.done === stats.total && errored === 0;

  const hasWebsiteData = stats.totalPages > 0;

  const websiteChips = useMemo(
    () =>
      [
        {
          key: 'new',
          label: t`New`,
          count: stats.newPages,
          status: WebsitePageStatus.New,
          variant: 'warning' as const,
        },
        {
          key: 'removed',
          label: t`Removed`,
          count: stats.removedPages,
          status: WebsitePageStatus.Removed,
          variant: 'danger' as const,
        },
      ].filter(chip => chip.count > 0),
    [stats.newPages, stats.removedPages, t],
  );

  const failedPillActive =
    activeDocumentPipelineFilter === 'errored' ||
    activeDocumentPipelineFilter === 'parsingError' ||
    activeDocumentPipelineFilter === 'embeddingError';

  const doneFilterActive = activeDocumentPipelineFilter === 'done';

  const summary = (
    <>
      <button
        type='button'
        className={cn(
          'text-sm tabular-nums',
          onDocumentPipelineFilterChange &&
            'cursor-pointer rounded px-1 hover:bg-muted/50',
          doneFilterActive && 'bg-accent font-medium ring-1 ring-primary',
        )}
        onClick={
          onDocumentPipelineFilterChange
            ? () => onDocumentPipelineFilterChange('done')
            : undefined
        }
      >
        <span>{t`Done`}</span>{' '}
        <strong>
          {stats.done}/{stats.total}
        </strong>
      </button>

      {errored > 0 && (
        <Badge
          variant='danger'
          size='sm'
          className={cn(
            'cursor-pointer',
            failedPillActive && 'ring-2 ring-danger',
          )}
          onClick={
            onDocumentPipelineFilterChange
              ? () => onDocumentPipelineFilterChange('errored')
              : undefined
          }
        >
          {errored} {t`failed`}
        </Badge>
      )}

      {inProgress > 0 && !isFullyComplete && (
        <button
          type='button'
          className={cn(
            'text-xs text-muted-fg',
            onDocumentPipelineFilterChange &&
              'cursor-pointer rounded px-1 hover:bg-muted/50',
            activeDocumentPipelineFilter === 'processing' &&
              'bg-accent font-medium',
          )}
          onClick={
            onDocumentPipelineFilterChange
              ? () => onDocumentPipelineFilterChange('processing')
              : undefined
          }
        >
          {t`${inProgress} processing`}
        </button>
      )}

      {hasWebsiteData &&
        websiteChips.map(chip => {
          const isChipActive = activeStatusFilter === chip.status;

          return (
            <Badge
              key={chip.key}
              variant={chip.variant}
              size='sm'
              className={cn(
                'cursor-pointer',
                isChipActive && 'ring-2 ring-primary',
              )}
              onClick={
                onStatusFilterChange
                  ? () => onStatusFilterChange(chip.status)
                  : undefined
              }
            >
              {chip.label} {chip.count}
            </Badge>
          );
        })}
    </>
  );

  return (
    <StatsSummaryShell
      icon={Workflow}
      label={t`Processing pipeline`}
      progressValue={stats.done}
      progressMax={stats.total > 0 ? stats.total : 100}
      completePercent={completePercent}
      isFullyComplete={isFullyComplete}
      summary={summary}
    >
      <div
        className={cn(
          'grid grid-cols-1 gap-3',
          hasWebsiteData && 'lg:grid-cols-2 lg:gap-5',
        )}
      >
        <section>
          <p className='mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-fg'>
            {t`Readiness`}
          </p>
          <div className='flex flex-col'>
            {readinessStats.map(stat => (
              <ReadinessRow
                key={stat.id}
                stat={stat}
                stats={stats}
                activeDocumentPipelineFilter={activeDocumentPipelineFilter}
                onDocumentPipelineFilterChange={onDocumentPipelineFilterChange}
              />
            ))}
          </div>
        </section>

        {hasWebsiteData && (
          <section>
            <p className='mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-fg'>
              {t`Website sync`}
            </p>
            <div className='flex flex-col'>
              {websiteStats.map(stat => (
                <WebsiteRow
                  key={stat.id}
                  stat={stat}
                  stats={stats}
                  activeStatusFilter={activeStatusFilter}
                  onStatusFilterChange={onStatusFilterChange}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    </StatsSummaryShell>
  );
});
